import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Lightbox } from "./Lightbox";

type Figure = {
  /** i18n key under `figures.items.*` (label + alt live in landing.json). */
  key: string;
  src: string;
  width: number;
  height: number;
};

const FIGURES: Figure[] = [
  { key: "forecast", src: "/landing-forecast-output.webp", width: 1760, height: 918 },
  { key: "evaluation", src: "/landing-evaluation.webp", width: 1600, height: 822 },
  { key: "distributions", src: "/landing-distributions.webp", width: 1100, height: 1267 },
];

function Thumbnail({
  figure,
  label,
  alt,
  enlargeLabel,
  onOpen,
  fill = false,
}: {
  figure: Figure;
  label: string;
  alt: string;
  enlargeLabel: string;
  onOpen: () => void;
  /** Stretch the panel to its grid cell's full height (image cover-fills it).
      Used for FIG.03 so its bottom border lines up with FIG.02's. */
  fill?: boolean;
}) {
  return (
    <figure
      className={`bp-panel relative flex flex-col overflow-hidden${fill ? " sm:h-full" : ""}`}
    >
      {/* Caption is real text (not part of the image), so it stays crisp and
          readable even when the thumbnail itself is small. */}
      <figcaption className="flex items-center justify-between gap-2 border-b border-[#1B2622] px-3 py-2">
        <span className="mono-label whitespace-nowrap text-[11px] text-[#9DACA4]">
          {label}
        </span>
        <span className="flex shrink-0 items-center gap-1.5 bp-mono text-[10px] uppercase tracking-[0.18em] text-[#4CD6C4]">
          <span className="bp-dot" aria-hidden /> live
        </span>
      </figcaption>
      <button
        type="button"
        onClick={onOpen}
        aria-label={`${enlargeLabel} — ${label}`}
        className={`group relative block flex-1 cursor-zoom-in p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#4CD6C4]${fill ? " sm:min-h-0" : ""}`}
      >
        <img
          src={figure.src}
          alt={alt}
          width={figure.width}
          height={figure.height}
          loading="lazy"
          className={`w-full rounded-sm transition-transform duration-300 group-hover:scale-[1.015] ${fill ? "h-auto sm:h-full sm:object-cover sm:object-top" : "h-auto"}`}
        />
        {/* Always-visible affordance so it's clear the image is clickable
            (the icon gently pulses; it brightens/grows on hover). */}
        <span className="pointer-events-none absolute bottom-2.5 right-2.5 flex items-center gap-1.5 rounded-full border border-[#4CD6C4]/40 bg-black/55 px-2.5 py-1 bp-mono text-[10px] uppercase tracking-wide text-[#E2EDE7] backdrop-blur-sm transition-all duration-200 group-hover:scale-105 group-hover:border-[#4CD6C4]/90 group-hover:bg-black/80">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
            className="h-3.5 w-3.5 text-[#4CD6C4] motion-safe:animate-pulse"
          >
            <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3" />
          </svg>
          {enlargeLabel}
        </span>
      </button>
    </figure>
  );
}

/**
 * The hero's product-output cluster: FIG.01 + FIG.02 stacked on the left,
 * FIG.03 (tall) on the right. Clicking any figure opens a full-size lightbox.
 */
export function HeroFigures() {
  const { t } = useTranslation("landing");
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const active = openIndex === null ? null : FIGURES[openIndex];

  const label = (f: Figure) => t(`figures.items.${f.key}.label`);
  const alt = (f: Figure) => t(`figures.items.${f.key}.alt`);
  const enlargeLabel = t("figures.enlarge");

  return (
    <div className="anim-fade-up" style={{ animationDelay: "240ms" }}>
      <div className="grid grid-cols-1 sm:grid-cols-2 items-stretch gap-3 lg:gap-4">
        <div className="flex flex-col gap-3 lg:gap-4">
          <Thumbnail
            figure={FIGURES[0]}
            label={label(FIGURES[0])}
            alt={alt(FIGURES[0])}
            enlargeLabel={enlargeLabel}
            onOpen={() => setOpenIndex(0)}
          />
          <Thumbnail
            figure={FIGURES[1]}
            label={label(FIGURES[1])}
            alt={alt(FIGURES[1])}
            enlargeLabel={enlargeLabel}
            onOpen={() => setOpenIndex(1)}
          />
        </div>
        <Thumbnail
          figure={FIGURES[2]}
          label={label(FIGURES[2])}
          alt={alt(FIGURES[2])}
          enlargeLabel={enlargeLabel}
          onOpen={() => setOpenIndex(2)}
          fill
        />
      </div>

      {active && (
        <Lightbox
          src={active.src}
          alt={alt(active)}
          label={label(active)}
          onClose={() => setOpenIndex(null)}
        />
      )}
    </div>
  );
}

export default HeroFigures;
